import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, useSyncExternalStore } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SmsIcon from "@mui/icons-material/Sms";
import { ApiError, refusalMessage } from "../../lib/api";
import { useTheme, type Theme } from "@mui/material/styles";
import BlockIcon from "@mui/icons-material/Block";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ShortlistSearchModal from "../messages/ShortlistSearchModal";
import SavedMessagePicker from '../messages/SavedMessagePicker';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import type { SmsTemplateSelection } from '../../services/clientSmsService';
import LinkIcon from "@mui/icons-material/Link";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ClientThreadMessage,
  ClientThreadResponse,
  attestClientSmsConsent,
  fetchClientSmsThread,
  markClientSmsThreadRead,
  sendClientSms,
} from "../../services/clientSmsService";
import {
  buildPublicShortlistUrl,
  getOrCreateClientShortlist,
} from "../../services/clientShortlistService";
import { smsShortlistLinks } from "../../lib/smsShortlistLinks";
import { smsLengthWarning, smsSegmentInfo } from "../../lib/smsSegments";
import ThreadStream from "./ThreadStream";
import { useCoordinatedRefresh } from "../../hooks/useCoordinatedRefresh";

// TRAIN-2 · one thread per client (train D4), on the system number.
//
// The suppressed state is rendered BEFORE anyone types: when consent_state is
// 'denied' the composer is disabled and an unreachable banner shows.
// outbound_guardrails' own rule: "a gate that only fires on send is a gate
// that lets someone write a message they were never allowed to send." The
// backend gate still enforces — this surface just refuses to set the trap.
//
// Permissions: the tab is mounted for manager/admin only (D9). This panel
// itself renders whatever the API lets it read; the API is the boundary.

import { createMessageDraft, type MessageDraftSession } from "../../lib/messageDraft";

interface ClientMessagesPanelProps {
  draftSession?: MessageDraftSession;
  clientId: string;
  clientName: string;
  /** The mobile Messages thread screen renders its own app bar with the
   * client's name + phone; hide the panel's inline header there so the
   * name doesn't appear twice. Defaults to true (drawer tab unchanged). */
  showHeader?: boolean;
  /** Ch78 · a drafted message to start the composer with (the queue's
   * "Check in" action). The human still reads it and taps Send. */
  initialDraft?: string;
  /** PKT-0873 · lets the Messages page drive THIS composer from its rail:
   * "Send Text" focuses it, and a list saved from the rail's "Create/Edit
   * List" lands its link in this draft (the same string the composer's own
   * shortlist button inserts). Draft only — nothing here sends. */
  handleRef?: React.Ref<ClientMessagesPanelHandle>;
}

export interface ClientMessagesPanelHandle {
  /** Focus the composer (and bring it into view). */
  focusComposer: () => void;
  /** Put the saved list's tracked link into the draft. Refused (false) where
   * the composer's own shortlist button is disabled: an opted-out or
   * phoneless thread cannot lawfully carry the text the link is for. */
  insertShortlistLink: (shortlist: { public_token: string }) => boolean;
}

const KIND_LABEL: Record<string, string> = {
  media_bounce: "Auto-reply · media bounce",
  help_reply: "Auto-reply · HELP",
};

// (The old STATUS_DISPLAY / DeliveryStatusCaption pair — a second, unreachable
// copy of the delivery captions — was deleted in #378. ThreadStream's
// DeliveryStatus is the only renderer of these strings since #374.)


// Bubble colors resolved THROUGH THE THEME, never hardcoded greys. The bug
// this replaces: `bgcolor: "grey.100"` (a static near-white) with
// `color: "text.primary"` — which in the DARK palette is #F1F5F9, near-white.
// Real customer texts rendered invisibly (chrome visible, body white-on-white).
// `getContrastText` makes legibility structural: whatever the background
// resolves to, the foreground is computed against it. Outbound uses the
// deeper brand shade in dark mode because primary.main (#6366F1) + white
// measures 4.47:1 — under WCAG AA; the contrast suite enforces 4.5 for all.
export function bubbleColors(
  theme: Theme,
  isInbound: boolean,
  isSystem: boolean
): { bg: string; fg: string } {
  const dark = theme.palette.mode === "dark";
  const bg = isInbound
    ? dark
      ? theme.palette.grey[800]
      : theme.palette.grey[100]
    : isSystem
    ? dark
      ? theme.palette.grey[700]
      : theme.palette.grey[200]
    : dark
    ? theme.palette.primary.dark
    : theme.palette.primary.main;
  return { bg, fg: theme.palette.getContrastText(bg) };
}


// R2/B1 — THE POLL MUST NEVER EAT A SENT MESSAGE.
//
// The bug this replaces: the 15s poll did `setThread(prev => { if (!prev)
// return data; return data; })` — a dead branch under a comment claiming it
// merged. Send at t=0, poll at t=15 answered from a replica (or a request
// already in flight before the send landed): the server's list has no such
// message, the operator's own bubble VANISHES, they assume the send failed and
// hit Send again — and the CLIENT GETS THE SAME TEXT TWICE. A duplicate text
// to a real customer is not a UI glitch.
//
// So: merge by id, and a message the client has but the server hasn't returned
// yet SURVIVES. Nothing that is on screen is ever removed by a poll; the poll
// may only add and refresh.
export function mergeThreadMessages(
  prev: ClientThreadResponse,
  next: ClientThreadResponse
): ClientThreadResponse {
  const nextIds = new Set(next.messages.map((m) => m.id));
  const localOnly = prev.messages.filter((m) => !nextIds.has(m.id));
  if (localOnly.length === 0) return next;
  const messages = [...next.messages, ...localOnly].sort((a, b) => {
    const at = a.created_at ? Date.parse(a.created_at) : 0;
    const bt = b.created_at ? Date.parse(b.created_at) : 0;
    return at - bt;
  });
  return { ...next, messages };
}


/** Ch78 · "✓ Texting consent on file · asked on the website form". The
 * source names HOW, in the operator's words, never the ledger's enum. */
export function consentGrantedCopy(source: string | null | undefined): string {
  const how: Record<string, string> = {
    website_form: "asked on the website form",
    inbound_first_message: "they texted us first",
    operator_attested: "recorded by a teammate",
    carrier_start: "they texted START",
  };
  const detail = source ? how[source] : undefined;
  return detail ? `✓ Texting consent on file · ${detail}` : "✓ Texting consent on file";
}

function ClientMessagesPanel({
  clientId,
  clientName,
  showHeader = true,
  initialDraft,
  handleRef,
  draftSession,
}: ClientMessagesPanelProps) {
  const [thread, setThread] = useState<ClientThreadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [localSession] = useState(() => createMessageDraft(initialDraft));
  const session = draftSession ?? localSession;
  const saved = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const { text: draft, sending, error: sendError, selections: templateSelections } = saved;
  const setDraft = (value: React.SetStateAction<string>) => session.update(previous => ({ text: typeof value === 'function' ? value(previous.text) : value }));
  const setSending = (value: boolean) => session.update({ sending: value });
  const setSendError = (value: string | null) => session.update({ error: value });
  const setTemplateSelections = (value: React.SetStateAction<SmsTemplateSelection[]>) => session.update(previous => ({ selections: typeof value === 'function' ? value(previous.selections) : value }));
  const sendLock = useRef(false);
  // Snapshot of message ids that were unread when the thread LOADED. The
  // mark-read call fires right after, but the "New" chips stay for this
  // visit — the operator should see what was new before it stops being new.
  const [unreadIds, setUnreadIds] = useState<Set<string>>(new Set());
  const [attestOpen, setAttestOpen] = useState(false);
  const [attestNote, setAttestNote] = useState("");
  const [attestBusy, setAttestBusy] = useState(false);
  const [attestError, setAttestError] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  useEffect(() => {
    if (initialDraft && !session.getSnapshot().text && !session.getSnapshot().sending) {
      session.update({ text: initialDraft });
    }
  }, [initialDraft, session]);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const [searchOpen, setSearchOpen] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const clientIdentityRef = useRef({ clientId, generation: 0 });
  if (clientIdentityRef.current.clientId !== clientId) {
    clientIdentityRef.current = {
      clientId,
      generation: clientIdentityRef.current.generation + 1,
    };
  }

  const load = useCallback(async () => {
    const identity = { ...clientIdentityRef.current };
    const isCurrentClient = () =>
      clientIdentityRef.current.clientId === identity.clientId &&
      clientIdentityRef.current.generation === identity.generation;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchClientSmsThread(clientId);
      if (!isCurrentClient()) return;
      setThread(data);
      const unread = data.messages.filter(
        (m) => m.direction === "in" && !m.read_at
      );
      setUnreadIds(new Set(unread.map((m) => m.id)));
      if (unread.length > 0) {
        // Team-side read state: opening the thread IS reading it. Best-effort —
        // a failed mark keeps messages unread for the next viewer, never louder.
        markClientSmsThreadRead(clientId).catch(() => {});
      }
    } catch (err: any) {
      if (isCurrentClient()) {
        setLoadError(err?.message || "Failed to load messages");
      }
    } finally {
      if (isCurrentClient()) setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshThread = useCallback(async () => {
    const identity = { ...clientIdentityRef.current };
    const isCurrentClient = () =>
      clientIdentityRef.current.clientId === identity.clientId &&
      clientIdentityRef.current.generation === identity.generation;
    try {
      const data = await fetchClientSmsThread(clientId);
      if (!isCurrentClient()) return;
      setThread((prev) => (prev ? mergeThreadMessages(prev, data) : data));
      // R2/N6: a reply that arrives via refresh is new exactly like one that
      // arrived before the panel opened. Keep its marker and team read state.
      const unread = data.messages.filter(
        (m) => m.direction === "in" && !m.read_at
      );
      if (unread.length > 0) {
        setUnreadIds((prev) => {
          const merged = new Set(prev);
          unread.forEach((m) => merged.add(m.id));
          return merged;
        });
        markClientSmsThreadRead(clientId).catch(() => {});
      }
    } catch {
      // A failed refresh keeps the last-good thread; the next tick/resume retries.
    }
  }, [clientId]);
  useCoordinatedRefresh(refreshThread, {
    pollMs: 15_000,
    requestOnMount: false,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: "end" });
  }, [thread?.messages.length]);

  const suppressed = thread?.consent_state === "denied";
  const noPhone = thread != null && !thread.client_phone;
  const uncertainSend = thread?.messages.some(m => m.status === "outcome_unknown") ?? false;

  const handleSend = async () => {
    const submitted = session.getSnapshot();
    const body = submitted.text.trim();
    if (!body || sendLock.current || session.getSnapshot().sending || uncertainSend || loading || loadError || suppressed || noPhone) return;
    sendLock.current = true;
    const identity = { ...clientIdentityRef.current };
    const current = () => clientIdentityRef.current.clientId === identity.clientId && clientIdentityRef.current.generation === identity.generation;
    setSending(true);
    setSendError(null);
    try {
      const sent = submitted.selections.length
        ? await sendClientSms(clientId, body, submitted.selections)
        : await sendClientSms(clientId, body);
      // Clear only the submitted revision. Rail insertion or a pending link
      // preparation may have added unsent content while this request ran.
      session.update(current => current.revision === submitted.revision
        ? { text: "", selections: [] }
        : {});
      if (!active.current || !current()) return;
      setThread((prev) =>
        prev ? { ...prev, messages: [...prev.messages, sent] } : prev
      );
    } catch (err: any) {
      if (active.current && current()) void refreshThread();
      // The backend refusal carries the gate's reason — surface it verbatim
      // rather than a generic failure, so a refused send reads as a decision.
      //
      // EVENT-2179 · this used to read ONLY the object-with-`message` shape, and
      // `typeof detail === "object"` is false for a string, so every plain-string
      // refusal fell through to `err.message` — which for an ApiError is only
      // `API error: <status>` (lib/api.ts:100). Tonight's deploy made sending
      // "assigned only" for every role, so Grant pressed Send on an unassigned
      // client and got the 403 raised at sms.py:459 — whose detail is the plain
      // string "Only the locator assigned to this client can text them" — printed
      // in the red banner as `API error: 403`.
      //
      // `refusalMessage` reads BOTH of this route's shapes: the plain strings
      // (sms.py:419/441/459/464/483/550) and the guardrail object, which does
      // carry `message` (sms.py:580-586). There is deliberately NO code map here:
      // this route sends no `code` key at all — its object shape is keyed
      // `reason`/`message` — so a map would be dead weight, and one copied from
      // the claim route would be a new defect wearing a fix's clothes.
      const said = refusalMessage(err);
      setSendError(
        err instanceof ApiError && err.status < 500
          ? said ?? `The send was refused (${err.status}) without a reason. Tell support if it keeps happening.`
          : "Send outcome unknown — check the thread before resending."
      );
    } finally {
      sendLock.current = false;
      setSending(false);
    }
  };

  const handleAttest = async () => {
    if (attestBusy || attestNote.trim().length < 5) return;
    setAttestBusy(true);
    setAttestError(null);
    try {
      await attestClientSmsConsent(clientId, attestNote.trim());
      setAttestOpen(false);
      setAttestNote("");
      await load();
    } catch (err: any) {
      const detail = err?.detail;
      setAttestError(
        (typeof detail === "string" && detail) ||
          (typeof detail === "object" && detail?.message) ||
          err?.message ||
          "Failed to record consent"
      );
    } finally {
      setAttestBusy(false);
    }
  };

  /**
   * Put the client's tracked shortlist URL into the DRAFT. Shared by the link
   * button and the search modal so both surfaces produce the identical string —
   * two call sites formatting the same URL differently is how a tracked link
   * quietly becomes an untracked one.
   *
   * 🔴 DRAFT ONLY. Nothing here sends. The human presses Send, and the server
   * re-checks consent at that moment (sms.py).
   */
  const insertShortlistUrl = (shortlist: { public_token: string }) => {
    const url = smsShortlistLinks(buildPublicShortlistUrl(shortlist.public_token));
    setDraft((d) => (d ? `${d.replace(/\s+$/, "")} ${url}` : url));
  };

  useImperativeHandle(handleRef, () => ({
    focusComposer: () => {
      const node = composerRef.current;
      if (!node) return;
      node.scrollIntoView?.({ block: "nearest" });
      node.focus();
    },
    insertShortlistLink: (shortlist) => {
      if (suppressed || noPhone) return false;
      insertShortlistUrl(shortlist);
      return true;
    },
  }));

  const handleInsertClientLink = async () => {
    if (linkBusy) return;
    setLinkBusy(true);
    setSendError(null);
    try {
      const shortlist = await getOrCreateClientShortlist(clientId);
      if (active.current) insertShortlistUrl(shortlist);
    } catch (err: any) {
      if (active.current) setSendError(err?.message || "Could not fetch the client link");
    } finally {
      setLinkBusy(false);
    }
  };

  // Count what will actually be sent (list links lose "https://" at send).
  const segments = smsSegmentInfo(smsShortlistLinks(draft));

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} data-testid="sms-thread-loading" />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert severity="error" sx={{ m: 1 }}>
        {loadError}
      </Alert>
    );
  }

  return (
    <Box
      data-testid="sms-panel"
      sx={{
        display: "flex", flexDirection: "column", flex: 1, height: "100%", minHeight: 0,
        overflow: "hidden", p: 1,
        '& > :not([data-testid="sms-thread"])': { flexShrink: 0 },
      }}
    >
      {showHeader && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <SmsIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">
            Messages · {clientName}
          </Typography>
          {thread?.client_phone && (
            <Chip size="small" label={thread.client_phone} variant="outlined" />
          )}
        </Stack>
      )}

      {suppressed && (
        <Alert
          severity="warning"
          icon={<BlockIcon fontSize="small" />}
          data-testid="sms-unreachable-banner"
          sx={{ mb: 1 }}
        >
          This client opted out of SMS (STOP on record). The thread is
          unreachable — texting them is not permitted from any surface.
        </Alert>
      )}
      {noPhone && (
        <Alert severity="info" sx={{ mb: 1 }}>
          This client has no phone number on file.
        </Alert>
      )}
      {/* Ch78 · the POSITIVE state finally renders. Before this, a person who
          ticked "yes, text me" on the form and one nobody had asked looked the
          same (nothing), and the only consent copy on the panel was the
          warning. Grant: "he checked YES — why isn't that the first thing I
          see?" One slim line, with HOW they consented. */}
      {thread?.consent_state === "granted" && (
        <Typography
          variant="caption"
          data-testid="sms-consent-granted-line"
          sx={{
            display: "block",
            mb: 1,
            fontWeight: 600,
            color: "success.main",
          }}
        >
          {consentGrantedCopy(thread.consent_source)}
        </Typography>
      )}
      {thread?.consent_state === "unknown" && !noPhone && (
        <Alert
          severity="info"
          data-testid="sms-consent-unknown-banner"
          sx={{ mb: 1 }}
          action={
            <Button
              size="small"
              data-testid="sms-attest-open"
              onClick={() => setAttestOpen(true)}
            >
              Record consent
            </Button>
          }
        >
          No texting consent on file — sends will be refused until consent is
          recorded (website form, the client texting first, or your
          attestation).
        </Alert>
      )}

      {thread?.lead_history_unavailable && (
        <Typography
          variant="caption"
          color="text.secondary"
          role="status"
          data-testid="sms-lead-history-unavailable"
          sx={{ mb: 1 }}
        >
          Earlier lead messages are temporarily unavailable. Current client messages are shown.
        </Typography>
      )}

      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }} data-testid="sms-thread">
        {thread && thread.messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No messages yet. Replies to the LocateNow number land here.
          </Typography>
        ) : (
          <ThreadStream
            messages={thread?.messages || []}
            unreadIds={unreadIds}
            onRetry={(body) => { setDraft(body); setTemplateSelections([]); }}
          />
        )}
        <div ref={bottomRef} />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }} alignItems="flex-start">
        <Tooltip title="Insert the client's tracked shortlist link">
          <span>
            <IconButton
              size="small"
              data-testid="sms-insert-link"
              disabled={suppressed || noPhone || sending || linkBusy}
              onClick={handleInsertClientLink}
              sx={{ mt: 0.5 }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Search & shortlist properties">
          <span>
            <IconButton
              size="small"
              data-testid="sms-open-shortlist-search"
              /* 🔴 THE SAME EXPRESSION AS THE COMPOSER AND THE SEND BUTTON.
                 Not a similar one — the same. If a thread is unreachable the
                 modal must not be openable at all, because a shortlist built
                 there ends in a message that cannot lawfully be sent. */
              disabled={suppressed || noPhone || sending || linkBusy}
              onClick={() => setSearchOpen(true)}
              sx={{ mt: 0.5 }}
            >
              <PlaylistAddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Insert a saved message">
          <span><IconButton size="small" aria-label="Insert a saved message"
            disabled={suppressed || noPhone || sending || templateSelections.length >= 10}
            onClick={() => setTemplatesOpen(true)} sx={{ mt: 0.5 }}>
            <TextSnippetIcon fontSize="small" />
          </IconButton></span>
        </Tooltip>
        <TextField
          sx={{ flex: '1 1 180px' }}
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder={
            suppressed ? "Client opted out — sending disabled" : "Text the client…"
          }
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (!e.target.value.trim()) setTemplateSelections([]); }}
          disabled={suppressed || noPhone || sending}
          inputRef={composerRef}
          inputProps={{ "data-testid": "sms-compose-input", maxLength: 1600 }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={loading || !!loadError || suppressed || noPhone || sending || uncertainSend || !draft.trim()}
          data-testid="sms-send-button"
        >
          {sending ? "Sending…" : "Send"}
        </Button>
      </Stack>

      {templatesOpen && !suppressed && !noPhone && <SavedMessagePicker
        key={clientId} clientId={clientId} onClose={() => setTemplatesOpen(false)}
        onInsert={(body, selection) => {
          if (!active.current) return;
          const textBody = smsShortlistLinks(body);
          const next = draft.trim() ? `${draft.trim()}\n\n${textBody}` : textBody;
          if (next.length > 1600) {
            setSendError('The combined draft exceeds 1,600 characters. Shorten it before inserting another message.');
            setTemplatesOpen(false); return;
          }
          setDraft(next); setTemplateSelections(prev => [...prev, selection]);
          setSendError(null); setTemplatesOpen(false);
        }}
      />}
      {/* Mounted only while open so a closed modal cannot hold a stale
          selection from a different client's thread. `suppressed || noPhone`
          already disables the trigger; this is the second gate, because an
          openable-by-URL future should not become a sendable one. */}
      {searchOpen && !suppressed && !noPhone && (
        <ShortlistSearchModal
          open={searchOpen}
          clientId={clientId}
          clientFirstName={(clientName || "").trim().split(/\s+/)[0] || "the client"}
          onClose={() => setSearchOpen(false)}
          onSaved={(shortlist) => insertShortlistUrl(shortlist)}
        />
      )}
      {draft.trim().length > 0 && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            display: "block",
            textAlign: "right",
            color: (t) =>
              smsLengthWarning(segments)
                ? t.palette.mode === "light"
                  ? t.palette.warning.dark
                  : t.palette.warning.light
                : t.palette.text.secondary,
          }}
          data-testid="sms-segment-counter"
          data-warning={smsLengthWarning(segments)}
        >
          {segments.units} chars · {segments.segments} segment
          {segments.segments === 1 ? "" : "s"}
          {segments.segments > 1 ? " (billed per segment)" : ""}
          {smsLengthWarning(segments) ? " · Length warning: a link is usually the cause" : ""}
        </Typography>
      )}
      {uncertainSend && <Alert severity="warning" sx={{ mt: 1 }} action={<Button onClick={() => void refreshThread()}>Check status</Button>}>Send outcome unknown — check before resending. Refresh the thread to check for a provider update.</Alert>}
      {sendError && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="sms-send-error">
          {sendError}
        </Alert>
      )}

      <Dialog open={attestOpen} onClose={() => !attestBusy && setAttestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record texting consent</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            Only record this if the client actually told you they want texts.
            Your name and this note become the permanent evidence on the
            consent ledger — it cannot be edited later, only revoked.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="How did they consent? (required)"
            placeholder='e.g. "Said yes on our phone call, Aug 7"'
            value={attestNote}
            onChange={(e) => setAttestNote(e.target.value)}
            inputProps={{ "data-testid": "sms-attest-note", maxLength: 500 }}
          />
          {attestError && (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="sms-attest-error">
              {attestError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttestOpen(false)} disabled={attestBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            data-testid="sms-attest-confirm"
            disabled={attestBusy || attestNote.trim().length < 5}
            onClick={handleAttest}
          >
            {attestBusy ? "Recording…" : "Record consent"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// A changed recipient always owns a fresh view; only its explicit session is retained.
export default function ClientMessagesPanelByClient(props: ClientMessagesPanelProps) {
  return <ClientMessagesPanel key={props.clientId} {...props} />;
}
